import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useModule, ModuleId, MODULES } from '../contexts/ModuleContext';
import toast from 'react-hot-toast';

interface ModuleGuardProps {
  moduleId: ModuleId;
  children: React.ReactNode;
}

/**
 * ModuleGuard - Route guard component that checks if user has access to a module.
 * If not, redirects to home page with a toast message.
 */
export function ModuleGuard({ moduleId, children }: ModuleGuardProps) {
  const { hasModuleAccess } = useModule();
  const navigate = useNavigate();

  const hasAccess = hasModuleAccess(moduleId);
  const moduleName = MODULES[moduleId]?.name || moduleId;

  useEffect(() => {
    if (!hasAccess) {
      toast.error(`You don't have access to ${moduleName}. Contact your organization owner.`);
      navigate('/', { replace: true });
    }
  }, [hasAccess, moduleName, navigate]);

  // Don't render children if user doesn't have access
  if (!hasAccess) {
    return null;
  }

  return <>{children}</>;
}

/**
 * Hook to check module access programmatically
 */
export function useModuleGuard(moduleId: ModuleId) {
  const { hasModuleAccess } = useModule();
  const navigate = useNavigate();

  const checkAccess = () => {
    if (!hasModuleAccess(moduleId)) {
      const moduleName = MODULES[moduleId]?.name || moduleId;
      toast.error(`You don't have access to ${moduleName}. Contact your organization owner.`);
      navigate('/', { replace: true });
      return false;
    }
    return true;
  };

  return {
    hasAccess: hasModuleAccess(moduleId),
    checkAccess,
  };
}
